import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../utils/apiClient';

/**
 * Custom hook for managing trending tokens data
 */
const useTrendingTokens = (options = {}) => {
  const {
    autoRefresh = true,
    refreshInterval = 5 * 60 * 1000, // 5 minutes
    limit = 50,
    minScore = 0,
    enabled = true
  } = options;

  const [trendingTokens, setTrendingTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [serviceStatus, setServiceStatus] = useState(null);

  const refreshIntervalRef = useRef(null);
  const abortControllerRef = useRef(null);

  /**
   * Fetch trending tokens from API
   */
  const fetchTrendingTokens = useCallback(async (showLoading = true) => {
    if (!enabled) return;

    try {
      // Cancel previous request if still pending
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      if (showLoading) {
        setLoading(true);
      }
      setError(null);

      const params = new URLSearchParams();
      if (limit) params.append('limit', limit.toString());
      if (minScore > 0) params.append('minScore', minScore.toString());

      const response = await apiClient.get(`/trending/tokens?${params.toString()}`, {
        signal: abortControllerRef.current.signal
      });

      if (response.success) {
        setTrendingTokens(response.data.tokens || []);
        setLastUpdated(response.data.lastUpdated ? new Date(response.data.lastUpdated) : new Date());
      } else {
        throw new Error(response.message || 'Failed to fetch trending tokens');
      }

    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error fetching trending tokens:', err);
        setError(err.message || 'Failed to fetch trending tokens');
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
      abortControllerRef.current = null;
    }
  }, [enabled, limit, minScore]);

  /**
   * Fetch service status
   */
  const fetchServiceStatus = useCallback(async () => {
    try {
      const response = await apiClient.get('/trending/status');
      if (response.success) {
        setServiceStatus(response.data);
      }
    } catch (err) {
      console.error('Error fetching trending service status:', err);
    }
  }, []);

  /**
   * Get specific trending token details
   */
  const getTrendingTokenDetails = useCallback(async (tokenAddress) => {
    try {
      const response = await apiClient.get(`/trending/tokens/${tokenAddress}`);
      return response.success ? response.data : null;
    } catch (err) {
      console.error('Error fetching trending token details:', err);
      return null;
    }
  }, []);

  /**
   * Trigger manual refresh
   */
  const refresh = useCallback(() => {
    fetchTrendingTokens(true);
  }, [fetchTrendingTokens]);

  /**
   * Check if a token is trending
   */
  const isTokenTrending = useCallback((tokenAddress) => {
    return trendingTokens.some(token => token.token_address === tokenAddress);
  }, [trendingTokens]);

  /**
   * Get trending data for a specific token
   */
  const getTrendingData = useCallback((tokenAddress) => {
    return trendingTokens.find(token => token.token_address === tokenAddress) || null;
  }, [trendingTokens]);

  /**
   * Get trending tokens by score range
   */
  const getTrendingByScore = useCallback((minScore, maxScore = 100) => {
    return trendingTokens.filter(token => 
      token.trend_score >= minScore && token.trend_score <= maxScore
    );
  }, [trendingTokens]);

  /**
   * Get trending tokens by detection source
   */
  const getTrendingBySource = useCallback((source) => {
    return trendingTokens.filter(token => token.detection_source === source);
  }, [trendingTokens]);

  // Setup auto-refresh
  useEffect(() => {
    if (!enabled || !autoRefresh) return;

    // Initial fetch
    fetchTrendingTokens(true);

    // Setup interval for background updates
    if (refreshInterval > 0) {
      refreshIntervalRef.current = setInterval(() => {
        fetchTrendingTokens(false); // Background refresh without loading state
      }, refreshInterval);
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [enabled, autoRefresh, refreshInterval, fetchTrendingTokens]);

  // Fetch service status on mount
  useEffect(() => {
    if (enabled) {
      fetchServiceStatus();
    }
  }, [enabled, fetchServiceStatus]);

  return {
    // Data
    trendingTokens,
    loading,
    error,
    lastUpdated,
    serviceStatus,

    // Actions
    refresh,
    fetchServiceStatus,
    getTrendingTokenDetails,

    // Utilities
    isTokenTrending,
    getTrendingData,
    getTrendingByScore,
    getTrendingBySource,

    // Stats
    totalTrending: trendingTokens.length,
    hotTokens: getTrendingByScore(80),
    trendingCount: getTrendingByScore(60, 79).length,
    risingCount: getTrendingByScore(40, 59).length
  };
};

/**
 * Hook for trending token statistics
 */
export const useTrendingStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get('/trending/stats');
      
      if (response.success) {
        setStats(response.data);
      } else {
        throw new Error(response.message || 'Failed to fetch trending stats');
      }

    } catch (err) {
      console.error('Error fetching trending stats:', err);
      setError(err.message || 'Failed to fetch trending stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refresh: fetchStats
  };
};

/**
 * Hook for trending token history
 */
export const useTrendingHistory = (hours = 24, limit = 100) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.append('hours', hours.toString());
      params.append('limit', limit.toString());

      const response = await apiClient.get(`/trending/history?${params.toString()}`);
      
      if (response.success) {
        setHistory(response.data.tokens || []);
      } else {
        throw new Error(response.message || 'Failed to fetch trending history');
      }

    } catch (err) {
      console.error('Error fetching trending history:', err);
      setError(err.message || 'Failed to fetch trending history');
    } finally {
      setLoading(false);
    }
  }, [hours, limit]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return {
    history,
    loading,
    error,
    refresh: fetchHistory
  };
};

export default useTrendingTokens;